import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Typography,
  Spin,
  message,
  Space,
  Breadcrumb,
  Tag,
  Popconfirm,
  Tabs,
  Result,
  Empty,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import type {
  Proposal,
  RequirementAnalysis,
  ProductInsight,
  PricingPlan,
  ReferenceInfluencerList,
  ProposalDocument,
  Chapter,
} from '../../types/proposal';
import { ProposalForm } from './ProposalForm';
import { RequirementTab } from './RequirementTab';
import { InsightTab } from './InsightTab';
import { PricingTab } from './PricingTab';
import { ReferenceTab } from './ReferenceTab';
import { ROUTES } from '../../config/routes';
import { POLL_CONFIG, showPollTimeoutNotification } from '../../utils/polling';
import { PROPOSAL_STATUS_CONFIG, AI_TASK_STATUS_CONFIG } from '../../constants/proposalStatus';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

// 状态Tag颜色映射
const getStatusTag = (status: string) => {
  const config = PROPOSAL_STATUS_CONFIG[status] || { text: status, color: 'default' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

// AI任务状态Tag
const getAITaskStatusTag = (status: string) => {
  const config = AI_TASK_STATUS_CONFIG[status] || { text: status, color: 'default' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

export const ProposalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  
  // 需求分析状态
  const [requirementAnalysis, setRequirementAnalysis] = useState<RequirementAnalysis | null>(null);
  const [requirementLoading, setRequirementLoading] = useState(false);
  const [requirementInput, setRequirementInput] = useState('');
  
  // 产品洞察状态
  const [productInsight, setProductInsight] = useState<ProductInsight | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  
  // 报价方案状态
  const [pricingPlan, setPricingPlan] = useState<PricingPlan | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [serviceFeeRate, setServiceFeeRate] = useState(10); // 默认 10%
  
  // 参考红人状态
  const [referenceList, setReferenceList] = useState<ReferenceInfluencerList | null>(null);
  const [referenceLoading, setReferenceLoading] = useState(false);
  
  // 轮询定时器引用
  const requirementTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const insightTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pricingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const referenceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const documentTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 提案文档状态
  const [document, setDocument] = useState<ProposalDocument | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentPolling, setDocumentPolling] = useState(false);

  // 获取需求分析结果
  const fetchRequirementAnalysis = useCallback(async (proposalId: string) => {
    try {
      const response = await proposalApi.getRequirementAnalysis(proposalId);
      console.log('[ProposalDetail] fetchRequirementAnalysis response:', response.data?.parsed_result);
      setRequirementAnalysis(response.data);
      return response.data;
    } catch (error) {
      console.error('fetchRequirementAnalysis error:', error);
      return null;
    }
  }, []);

  // 获取产品洞察结果
  const fetchProductInsight = useCallback(async (proposalId: string) => {
    try {
      const response = await proposalApi.getProductInsight(proposalId);
      setProductInsight(response.data);
      return response.data;
    } catch (error) {
      console.error('fetchProductInsight error:', error);
      return null;
    }
  }, []);

  // 获取报价方案
  const fetchPricing = useCallback(async (proposalId: string) => {
    try {
      const response = await proposalApi.getPricing(proposalId);
      setPricingPlan(response.data);
      if (response.data?.service_fee_rate !== undefined) {
        setServiceFeeRate(response.data.service_fee_rate * 100); // 转为百分比
      }
      return response.data;
    } catch (error) {
      console.error('fetchPricing error:', error);
      return null;
    }
  }, []);

  // 获取参考红人
  const fetchReferences = useCallback(async (proposalId: string) => {
    try {
      const response = await proposalApi.getReferences(proposalId);
      setReferenceList(response.data);
      return response.data;
    } catch (error) {
      console.error('fetchReferences error:', error);
      return null;
    }
  }, []);

  const fetchProposal = useCallback(async (proposalId: string) => {
    setLoading(true);
    try {
      const response = await proposalApi.getProposal(proposalId);
      setProposal(response.data);
      // 获取需求分析结果
      await fetchRequirementAnalysis(proposalId);
      // 获取产品洞察结果
      await fetchProductInsight(proposalId);
      // 获取报价方案
      await fetchPricing(proposalId);
      // 获取参考红人
      await fetchReferences(proposalId);
    } catch (error) {
      message.error('获取提案详情失败');
      navigate(ROUTES.PROPOSALS);
    } finally {
      setLoading(false);
    }
  }, [navigate, fetchRequirementAnalysis, fetchProductInsight, fetchPricing, fetchReferences]);

  useEffect(() => {
    if (id) {
      fetchProposal(id);
    }
    // 清理定时器
    return () => {
      if (requirementTimerRef.current) {
        clearInterval(requirementTimerRef.current);
      }
      if (insightTimerRef.current) {
        clearInterval(insightTimerRef.current);
      }
      if (pricingTimerRef.current) {
        clearInterval(pricingTimerRef.current);
      }
      if (referenceTimerRef.current) {
        clearInterval(referenceTimerRef.current);
      }
      if (documentTimerRef.current) {
        clearInterval(documentTimerRef.current);
      }
    };
  }, [id, fetchProposal]);

  // 开始需求分析
  const handleStartRequirementAnalysis = async () => {
    if (!id || !requirementInput.trim()) {
      message.warning('请输入需求内容');
      return;
    }

    setRequirementLoading(true);
    try {
      const response = await proposalApi.analyzeRequirements(id, {
        raw_content: requirementInput,
      });
      message.success('AI需求分析已启动');
      setRequirementAnalysis(response.data);
      
      // 开始轮询
      startRequirementPolling();
    } catch (error) {
      message.error('启动需求分析失败');
      setRequirementLoading(false);
    }
  };

  // 需求分析轮询
  const startRequirementPolling = useCallback(() => {
    // 清除之前的定时器
    if (requirementTimerRef.current) {
      clearInterval(requirementTimerRef.current);
    }

    let pollAttempts = 0;
    requirementTimerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (requirementTimerRef.current) {
          clearInterval(requirementTimerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-requirement', () => {
          startRequirementPolling();
        });
        setRequirementLoading(false);
        return;
      }
      const result = await fetchRequirementAnalysis(id!);
      if (result && (result.status === 'draft' || result.status === 'confirmed' || result.status === 'failed')) {
        if (requirementTimerRef.current) {
          clearInterval(requirementTimerRef.current);
        }
        setRequirementLoading(false);
        if (result.status === 'failed') {
          message.error('需求分析失败：' + (result.error_message || '未知错误'));
        } else {
          message.success('需求分析完成');
        }
      }
    }, POLL_CONFIG.INTERVAL);
  }, [fetchRequirementAnalysis, id]);

  // 开始产品洞察
  const handleStartProductInsight = async () => {
    if (!id) {
      message.warning('提案ID不存在');
      return;
    }

    if (!requirementAnalysis || (requirementAnalysis.status !== 'draft' && requirementAnalysis.status !== 'confirmed')) {
      message.warning('请先完成需求分析');
      return;
    }

    setInsightLoading(true);
    try {
      const response = await proposalApi.generateProductInsight(id);
      message.success('AI产品洞察已启动');
      setProductInsight(response.data);
      
      // 开始轮询
      startInsightPolling();
    } catch (error) {
      message.error('启动产品洞察失败');
      setInsightLoading(false);
    }
  };

  // 产品洞察轮询
  const startInsightPolling = useCallback(() => {
    // 清除之前的定时器
    if (insightTimerRef.current) {
      clearInterval(insightTimerRef.current);
    }

    let pollAttempts = 0;
    insightTimerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (insightTimerRef.current) {
          clearInterval(insightTimerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-insight', () => {
          startInsightPolling();
        });
        setInsightLoading(false);
        return;
      }
      const result = await fetchProductInsight(id!);
      if (result && (result.status === 'completed' || result.status === 'failed')) {
        if (insightTimerRef.current) {
          clearInterval(insightTimerRef.current);
        }
        setInsightLoading(false);
        if (result.status === 'failed') {
          message.error('产品洞察失败：' + (result.error_message || '未知错误'));
        } else {
          message.success('产品洞察完成');
        }
      }
    }, POLL_CONFIG.INTERVAL);
  }, [fetchProductInsight, id]);

  // 开始生成报价
  const handleStartPricing = async () => {
    if (!id) {
      message.warning('提案ID不存在');
      return;
    }

    setPricingLoading(true);
    try {
      await proposalApi.generatePricing(id, { service_fee_rate: serviceFeeRate / 100 });
      message.success('AI报价生成已启动');
      
      // 开始轮询
      startPricingPolling();
    } catch (error) {
      message.error('启动报价生成失败');
      setPricingLoading(false);
    }
  };

  // 报价轮询
  const startPricingPolling = useCallback(() => {
    if (pricingTimerRef.current) {
      clearInterval(pricingTimerRef.current);
    }

    let pollAttempts = 0;
    pricingTimerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (pricingTimerRef.current) {
          clearInterval(pricingTimerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-pricing', () => {
          startPricingPolling();
        });
        setPricingLoading(false);
        return;
      }
      const result = await fetchPricing(id!);
      if (result && (result.status === 'completed' || result.status === 'failed')) {
        if (pricingTimerRef.current) {
          clearInterval(pricingTimerRef.current);
        }
        setPricingLoading(false);
        if (result.status === 'failed') {
          message.error('报价生成失败');
        } else {
          message.success('报价生成完成');
        }
      }
    }, POLL_CONFIG.INTERVAL);
  }, [fetchPricing, id]);

  // 更新服务费率
  const handleServiceFeeChange = async (value: number) => {
    setServiceFeeRate(value);
    if (pricingPlan?.id) {
      try {
        await proposalApi.updatePricing(pricingPlan.id, {
          service_fee_rate: value / 100,
        });
      } catch (error) {
        message.error('更新服务费率失败');
      }
    }
  };

  // 开始生成参考红人
  const handleStartReferences = async () => {
    if (!id) {
      message.warning('提案ID不存在');
      return;
    }

    setReferenceLoading(true);
    try {
      await proposalApi.generateReferences(id);
      message.success('AI参考红人推荐已启动');
      
      // 开始轮询
      startReferencePolling();
    } catch (error) {
      message.error('启动参考红人生成失败');
      setReferenceLoading(false);
    }
  };

  // 参考红人轮询
  const startReferencePolling = useCallback(() => {
    if (referenceTimerRef.current) {
      clearInterval(referenceTimerRef.current);
    }

    let pollAttempts = 0;
    referenceTimerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (referenceTimerRef.current) {
          clearInterval(referenceTimerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-reference', () => {
          startReferencePolling();
        });
        setReferenceLoading(false);
        return;
      }
      const result = await fetchReferences(id!);
      if (result && (result.status === 'completed' || result.status === 'failed')) {
        if (referenceTimerRef.current) {
          clearInterval(referenceTimerRef.current);
        }
        setReferenceLoading(false);
        if (result.status === 'failed') {
          message.error('参考红人生成失败');
        } else {
          message.success('参考红人生成完成');
        }
      }
    }, POLL_CONFIG.INTERVAL);
  }, [fetchReferences, id]);

  // 删除参考红人
  const handleDeleteReference = async (referenceId: string) => {
    try {
      await proposalApi.deleteReference(referenceId);
      message.success('删除成功');
      if (id) {
        await fetchReferences(id);
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 获取提案文档
  const fetchDocument = useCallback(async (proposalId: string) => {
    try {
      const response = await proposalApi.getDocument(proposalId);
      setDocument(response.data);
      return response.data;
    } catch (error) {
      console.error('fetchDocument error:', error);
      return null;
    }
  }, []);

  // 生成提案文档
  const handleGenerateDocument = async (format: 'excel' | 'ppt') => {
    if (!id) {
      message.warning('提案ID不存在');
      return;
    }

    setDocumentLoading(true);
    setDocumentPolling(true);
    try {
      const response = await proposalApi.generateDocument(id, { format });
      setDocument(response.data);
      message.success(`AI${format === 'excel' ? 'Excel报价单' : '完整提案'}生成已启动`);

      // 开始轮询
      startDocumentPolling();
    } catch (error) {
      message.error('启动文档生成失败');
      setDocumentLoading(false);
      setDocumentPolling(false);
    }
  };

  // 文档轮询
  const startDocumentPolling = useCallback(() => {
    if (documentTimerRef.current) {
      clearInterval(documentTimerRef.current);
    }

    let pollAttempts = 0;
    documentTimerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (documentTimerRef.current) {
          clearInterval(documentTimerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-document', () => {
          startDocumentPolling();
        });
        setDocumentLoading(false);
        setDocumentPolling(false);
        return;
      }
      const result = await fetchDocument(id!);
      if (result && (result.status === 'completed' || result.status === 'failed')) {
        if (documentTimerRef.current) {
          clearInterval(documentTimerRef.current);
        }
        setDocumentLoading(false);
        setDocumentPolling(false);
        if (result.status === 'failed') {
          message.error('文档生成失败：' + (result.error_message || '未知错误'));
        } else {
          message.success('文档生成完成');
        }
      }
    }, POLL_CONFIG.INTERVAL);
  }, [fetchDocument, id]);

  // 下载文档
  const handleDownloadDocument = async () => {
    if (!document?.id) return;
    try {
      const response = await proposalApi.downloadDocument(document.id);
      const url = URL.createObjectURL(response.data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `proposal_${id}.${document.format === 'excel' ? 'xlsx' : 'pptx'}`;
      a.click();
      URL.revokeObjectURL(url);
      // 更新下载次数
      setDocument(prev => prev ? { ...prev, download_count: prev.download_count + 1 } : null);
    } catch (error) {
      message.error('下载失败');
    }
  };

  // 简单的 Markdown 渲染
  const renderMarkdown = (text: string): string => {
    return text
      // 标题
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      // 粗体
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // 斜体
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // 无序列表
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // 有序列表
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // 换行
      .replace(/\n/g, '<br/>');
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await proposalApi.deleteProposal(id);
      message.success('提案删除成功');
      navigate(ROUTES.PROPOSALS);
    } catch (error) {
      message.error('删除提案失败');
    }
  };

  const handleEditSuccess = () => {
    setEditFormOpen(false);
    if (id) {
      fetchProposal(id);
    }
  };

  // 缓存各Tab的onRefresh回调，避免不必要的重渲染
  const handleRefreshRequirement = useCallback(() => {
    if (id) fetchRequirementAnalysis(id);
  }, [id, fetchRequirementAnalysis]);

  const handleRefreshInsight = useCallback(async () => {
    if (!id) return;
    const result = await fetchProductInsight(id);
    // 如果状态是 processing，自动启动轮询直到 completed/failed
    if (result && result.status === 'processing') {
      startInsightPolling();
    }
  }, [id, fetchProductInsight, startInsightPolling]);

  const handleRefreshPricing = useCallback(() => {
    if (id) fetchPricing(id);
  }, [id, fetchPricing]);

  const handleRefreshReferences = useCallback(() => {
    if (id) fetchReferences(id);
  }, [id, fetchReferences]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={4}>提案不存在</Title>
        <Button onClick={() => navigate(ROUTES.PROPOSALS)}>返回列表</Button>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'basic',
      label: '基本信息',
      children: (
        <Card>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="提案标题">{proposal.title}</Descriptions.Item>
            <Descriptions.Item label="关联客户">
              {proposal.customer?.company_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(requirementAnalysis?.status === 'confirmed' ? 'confirmed' : proposal.status)}
            </Descriptions.Item>
            <Descriptions.Item label="创建者">{proposal.created_by_name || proposal.created_by || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {new Date(proposal.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {proposal.updated_at ? new Date(proposal.updated_at).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'requirement',
      label: (
        <span>
          需求解构
          {requirementAnalysis && getAITaskStatusTag(requirementAnalysis.status)}
        </span>
      ),
      children: (
        <RequirementTab
          proposalId={id!}
          requirement={requirementAnalysis}
          onRefresh={handleRefreshRequirement}
        />
      ),
    },
    {
      key: 'insight',
      label: (
        <span>
          产品洞察
          {productInsight && getAITaskStatusTag(productInsight.status)}
        </span>
      ),
      children: (
        <InsightTab
          proposalId={id!}
          insight={productInsight}
          requirementStatus={requirementAnalysis?.status}
          onRefresh={handleRefreshInsight}
        />
      ),
    },
    // Tab 4: 报价方案
    {
      key: 'pricing',
      label: (
        <span>
          <DollarOutlined style={{ marginRight: 4 }} />
          报价方案
          {pricingPlan && getAITaskStatusTag(pricingPlan.status)}
        </span>
      ),
      children: (
        <PricingTab
          proposalId={id!}
          pricing={pricingPlan}
          requirement={requirementAnalysis}
          onRefresh={handleRefreshPricing}
        />
      ),
    },
    // Tab 5: 参考红人
    {
      key: 'references',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 4 }} />
          参考红人
          {referenceList && getAITaskStatusTag(referenceList.status)}
        </span>
      ),
      children: (
        <ReferenceTab
          proposalId={id!}
          references={referenceList}
          onRefresh={handleRefreshReferences}
        />
      ),
    },
    // Tab 6: 提案文档
    {
      key: 'document',
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 4 }} />
          提案文档
          {document && (
            <Tag
              color={document.status === 'completed' ? 'success' : document.status === 'failed' ? 'error' : 'processing'}
              style={{ marginLeft: 4 }}
            >
              {document.status === 'completed' ? '已生成' : document.status === 'failed' ? '失败' : '生成中'}
            </Tag>
          )}
        </span>
      ),
      children: (
        <Card>
          {/* 生成按钮区域 */}
          <div style={{ marginBottom: 24 }}>
            <Space size="middle">
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={() => handleGenerateDocument('excel')}
                loading={documentPolling && document?.format === 'excel'}
                disabled={documentPolling}
              >
                {document?.format === 'excel' && document?.status === 'completed' ? '重新生成 Excel 报价单' : '生成 Excel 报价单'}
              </Button>
              <Button
                icon={<FilePptOutlined />}
                onClick={() => handleGenerateDocument('ppt')}
                loading={documentPolling && document?.format === 'ppt'}
                disabled={documentPolling}
              >
                {document?.format === 'ppt' && document?.status === 'completed' ? '重新生成完整提案' : '生成完整提案'}
              </Button>
            </Space>
          </div>

          {/* 加载状态 */}
          {documentPolling && document?.status === 'generating' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">正在生成中，请稍候...</Text>
              </div>
            </div>
          )}

          {/* 生成失败 */}
          {document?.status === 'failed' && (
            <Result
              status="error"
              title="文档生成失败"
              subTitle={document.error_message || '文档生成过程中发生错误'}
              extra={
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => handleGenerateDocument(document.format)}
                >
                  重试
                </Button>
              }
            />
          )}

          {/* Excel 结果区域 */}
          {document?.format === 'excel' && document?.status === 'completed' && (
            <div>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 20 }} />
                <Text strong>Excel 报价单已生成</Text>
              </div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="文件大小">
                    {document.file_size ? `${(document.file_size / 1024).toFixed(2)} KB` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="生成时间">
                    {new Date(document.created_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="下载次数">
                    {document.download_count || 0} 次
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadDocument}
              >
                下载 Excel
              </Button>
            </div>
          )}

          {/* PPT 内容预览区域（含章节预览） */}
          {document?.format === 'ppt' && document?.status === 'completed' && document.content && (
            <div>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 20 }} />
                <Text strong>完整提案已生成</Text>
                <Text type="secondary">
                  (生成时间: {new Date(document.content.generated_at || document.created_at).toLocaleString()})
                </Text>
              </div>

              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="文件大小">
                    {document.file_size ? `${(document.file_size / 1024).toFixed(2)} KB` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="生成时间">
                    {new Date(document.created_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="下载次数">
                    {document.download_count || 0} 次
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {document.content.chapters && document.content.chapters.length > 0 && (
                <Collapse
                  accordion
                  items={document.content.chapters
                    .slice()
                    .sort((a: Chapter, b: Chapter) => a.order - b.order)
                    .map((chapter: Chapter, index: number) => ({
                      key: String(chapter.order || index),
                      label: (
                        <Space>
                          <Tag color="blue">{chapter.order || index + 1}</Tag>
                          <Text strong>{chapter.title}</Text>
                        </Space>
                      ),
                      children: (
                        <div
                          style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(chapter.content) }}
                        />
                      ),
                    }))}
                  style={{ marginBottom: 16 }}
                />
              )}

              <Space>
                <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                  打印
                </Button>
                <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadDocument}>
                  下载 PPT
                </Button>
              </Space>
            </div>
          )}

          {/* PPT 已完成但无章节预览（仅文件下载） */}
          {document?.format === 'ppt' && document?.status === 'completed' && !document.content && (
            <div>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 20 }} />
                <Text strong>完整提案 PPT 已生成</Text>
              </div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="文件大小">
                    {document.file_size ? `${(document.file_size / 1024).toFixed(2)} KB` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="生成时间">
                    {new Date(document.created_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="下载次数">
                    {document.download_count || 0} 次
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadDocument}
              >
                下载 PPT
              </Button>
            </div>
          )}

          {/* 暂无数据 */}
          {!document && !documentPolling && !documentLoading && (
            <Empty description="暂无提案文档，请点击上方按钮生成" />
          )}
        </Card>
      ),
    },
  ];

  return (
    <div>
      {/* 面包屑导航 */}
      <Breadcrumb
        items={[
          { title: <a onClick={() => navigate(ROUTES.PROPOSALS)}>提案管理</a> },
          { title: proposal.title },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* 头部 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(ROUTES.PROPOSALS)}
            style={{ marginRight: 16 }}
          >
            返回
          </Button>
          <Title level={4} style={{ margin: 0, marginRight: 12 }}>
            {proposal.title}
          </Title>
          {getStatusTag(proposal.status)}
        </div>
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditFormOpen(true)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此提案吗？删除后不可恢复。"
            onConfirm={handleDelete}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Tab区域 */}
      <Tabs defaultActiveKey="basic" items={tabItems} />

      {/* 编辑表单 */}
      <ProposalForm
        open={editFormOpen}
        proposalId={proposal.id}
        onClose={() => setEditFormOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
